import type { SkillDraft } from '../types/agent'

/**
 * A skill's SKILL.md read into the editor's fields. The server rebuilds the
 * file from these on save (agenthost.BuildSkillMD, the same shape
 * hoist_skill.ts writes), so the editor never writes frontmatter itself.
 */
export interface SkillMarkdownFields {
  name: string
  description: string
  triggers: string[]
  body: string
}

/** Read SKILL.md: `name:`, `description:`, a `triggers:` list, then the body. */
export function splitSkillMarkdown(md: string): SkillMarkdownFields {
  const text = md.replace(/\r\n/g, '\n')
  const out: SkillMarkdownFields = { name: '', description: '', triggers: [], body: text.trim() }
  if (!text.startsWith('---\n')) return out
  const end = text.indexOf('\n---', 4)
  if (end < 0) return out
  const front = text.slice(4, end).split('\n')
  out.body = text.slice(end + 4).trim()
  let inTriggers = false
  for (const line of front) {
    const item = line.match(/^\s+-\s+(.*)$/)
    if (inTriggers && item) {
      out.triggers.push(item[1].trim())
      continue
    }
    inTriggers = false
    const kv = line.match(/^([A-Za-z_-]+):\s*(.*)$/)
    if (!kv) continue
    const [, key, value] = kv
    if (key === 'name') out.name = value.trim()
    else if (key === 'description') out.description = value.trim()
    else if (key === 'triggers') inTriggers = true
  }
  return out
}

/**
 * The SKILL.md body's sections, in order (Adam, 9 Oct): explicit instructions
 * a fresh session follows without the chat. The same as
 * agenthost.SkillSections, and the sections hoist_skill asks the agent for.
 */
export const SKILL_SECTIONS = ['When to use', 'Inputs', 'Files', 'Steps', 'Checks']

/** The body "Write one from scratch" starts from (agenthost.SkillBodyScaffold). */
export const SKILL_SCAFFOLD = [
  '## When to use', '', '- The requests this skill is for.', '- What it is not for.', '',
  '## Inputs', '', '- What the user gives or chooses, and the default when they do not say.', '',
  '## Files', '', '- `.claude/skills/<name>/files/<file>`: what it is, and the step that uses it.', '',
  '## Steps', '', '1. One action per step, with the exact command, tool call or setting.', '2. The next action.', '',
  '## Checks', '', '- What to verify before handing the result over.', '',
].join('\n')

/** Base64 of raw bytes (a template the user adds keeps every byte). */
export function bytesToBase64(bytes: Uint8Array): string {
  let bin = ''
  bytes.forEach((b) => { bin += String.fromCharCode(b) })
  return window.btoa(bin)
}

/** Base64 of UTF-8 text (bundle file contents travel as base64). */
export function encodeBase64Text(text: string): string {
  return bytesToBase64(new TextEncoder().encode(text))
}

/** A file from the user's computer as base64. */
export function readFileAsBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = () => resolve(bytesToBase64(new Uint8Array(reader.result as ArrayBuffer)))
    reader.onerror = () => reject(reader.error ?? new Error(`could not read ${file.name}`))
    reader.readAsArrayBuffer(file)
  })
}

/** UTF-8 text from base64; '' when it is not valid base64. */
export function decodeBase64Text(b64: string): string {
  try {
    const bin = window.atob(b64)
    return new TextDecoder().decode(Uint8Array.from(bin, (c) => c.charCodeAt(0)))
  } catch {
    return ''
  }
}

const SKILL_MD = 'SKILL.md'
const MANIFEST = 'skill.manifest.json'

/**
 * The editor's starting form for a stored skill: SKILL.md split into fields
 * (the row's name and description where the file lacks them), and the other
 * files. The manifest is the server's to write, so it is left out.
 */
export function draftFromBundle(
  row: { name: string; description: string },
  files: { path: string; content: string }[],
): Required<SkillDraft> {
  const md = files.find((f) => f.path === SKILL_MD)
  const fields = md ? splitSkillMarkdown(decodeBase64Text(md.content)) : { name: '', description: '', triggers: [], body: '' }
  return {
    name: fields.name || row.name,
    description: fields.description || row.description,
    triggers: fields.triggers,
    body: fields.body,
    files: files.filter((f) => f.path !== SKILL_MD && f.path !== MANIFEST),
  }
}

/**
 * The first message of a skill test chat. It names the skill by id, so a
 * draft (which search_skills never lists) still installs, and has the agent
 * read the SKILL.md before the user's request, so the test is the skill
 * followed cold.
 */
export function testChatMessage(skill: { id: string; name: string }, prompt: string): string {
  return [
    `This chat tests my skill "${skill.name}".`,
    `Install it with install_skill (name "${skill.name}", id "${skill.id}"), read .claude/skills/${skill.name}/SKILL.md, and follow it for this request:`,
    '',
    prompt.trim(),
  ].join('\n')
}
